import '@fontsource-variable/inter';
import '@fontsource-variable/plus-jakarta-sans';
import '@fontsource/cormorant-garamond/500.css';
import '@fontsource/cormorant-garamond/600.css';
import '@fontsource/cormorant-garamond/700.css';
import './styles/app.css';

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './i18n';
import { setUnauthenticatedHandler } from './lib/api';
import { createQueryClient } from './lib/queryClient';
import { applyTheme, readStoredTheme } from './lib/theme';
import { routeTree } from './routeTree.gen';

const queryClient = createQueryClient();

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// 401 côté client : retour à la connexion en mémorisant la page demandée.
setUnauthenticatedHandler(() => {
  queryClient.setQueryData(['auth', 'me'], null);
  const current = `${window.location.pathname}${window.location.search}`;
  if (window.location.pathname !== '/login') {
    void router.navigate({ to: '/login', search: { redirect: current, reason: 'expired' } });
  }
});

applyTheme(readStoredTheme());

// Développement seulement : routeur et cache exposés pour la recette visuelle
// sous mocks (`window.__horeviaMocks.signInAs(...)` puis navigation).
if (import.meta.env.DEV) {
  Object.assign(window, { __horeviaRouter: router, __horeviaQueryClient: queryClient });
}

/**
 * Mocks MSW : activés explicitement par `VITE_USE_MOCKS=true` (dev et e2e).
 * Tant que l'API n'existe pas, ils sont aussi actifs par défaut en développement ;
 * `VITE_USE_MOCKS=false` permet de brancher l'API réelle via le proxy Vite.
 */
const useMocks =
  import.meta.env.VITE_USE_MOCKS === 'true' ||
  (import.meta.env.DEV && import.meta.env.VITE_USE_MOCKS !== 'false');

async function bootstrap() {
  if (useMocks) {
    const { worker } = await import('./mocks/browser');
    await worker.start({ onUnhandledRequest: 'bypass', quiet: true });
  }

  const container = document.getElementById('root');
  if (!container) throw new Error('Élément racine introuvable.');

  createRoot(container).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
